import React, { useState } from 'react';
import classNames from 'classnames';
import { breakpoints, useToggle, useWindowSize } from '@openedx/paragon';
import { useIntl } from '@edx/frontend-platform/i18n';

import { useModel } from '@src/generic/model-store';
import { LOADING } from '@src/constants';
import PageLoading from '@src/generic/PageLoading';
import { useCourseOutlineSidebar } from '@src/courseware/course/sidebar/sidebars/course-outline/hooks';
import { ID } from '@src/courseware/course/sidebar/sidebars/course-outline/constants';
import messages from '@src/courseware/course/sidebar/sidebars/course-outline/messages';

import SidebarSection from './SidebarSection';
import SidebarSequence from './SidebarSequence';
import OutlineHeader from './OutlineHeader';

const OutlineTray = () => {
  const intl = useIntl();
  const [selectedSection, setSelectedSection] = useState(null);
  const [isDisplaySequenceLevel, setDisplaySequenceLevel, setDisplaySectionLevel] = useToggle(true);
  const shouldDisplayFullScreen = useWindowSize().width < breakpoints.medium.minWidth;

  const {
    courseId,
    unitId,
    isEnabledSidebar,
    currentSidebar,
    isActiveEntranceExam,
    handleToggleCollapse,
    courseOutlineStatus,
    activeSequenceId,
    sections,
    sequences,
  } = useCourseOutlineSidebar();

  const {
    sectionId: activeSectionId,
  } = useModel('sequences', activeSequenceId);

  const sectionsIds = Object.keys(sections);
  const sequenceIds = sections[selectedSection || activeSectionId]?.sequenceIds || [];
  const backButtonTitle = sections[selectedSection || activeSectionId]?.title;

  const handleBackToSectionLevel = () => {
    setDisplaySectionLevel();
    setSelectedSection(null);
  };

  const handleSelectSection = (id) => {
    setDisplaySequenceLevel();
    setSelectedSection(id);
  };

  if (!isEnabledSidebar || isActiveEntranceExam || currentSidebar !== ID) {
    return null;
  }

  if (courseOutlineStatus === LOADING) {
    return (
      <div className={classNames('outline-sidebar-wrapper', {
        'flex-shrink-0 fixed-width': !shouldDisplayFullScreen,
        'bg-white m-0 fixed-top w-100 vh-100': shouldDisplayFullScreen,
      })}
      >
        <section className="outline-sidebar w-100">
          <OutlineHeader
            {...{
              isDisplaySequenceLevel,
              backButtonTitle,
              shouldDisplayFullScreen,
              handleBackToSectionLevel,
              handleToggleCollapse,
            }}
          />
          <PageLoading
            srMessage={intl.formatMessage(messages.loading)}
          />
        </section>
      </div>
    );
  }

  return (
    <div className={classNames('outline-sidebar-wrapper', {
      'flex-shrink-0 mr-4 h-auto fixed-width': !shouldDisplayFullScreen,
      'm-0 fixed-top w-100 vh-100': shouldDisplayFullScreen,
    })}
    >
      <section className="w-100">
        <OutlineHeader
          {...{
            isDisplaySequenceLevel,
            backButtonTitle,
            shouldDisplayFullScreen,
            handleBackToSectionLevel,
            handleToggleCollapse,
          }}
        />
        <ol id="outline-sidebar-outline" className="list-unstyled mt-1">
          {isDisplaySequenceLevel ? sequenceIds.map((sequenceId) => (
            <SidebarSequence
              key={sequenceId}
              courseId={courseId}
              sequence={sequences[sequenceId]}
              defaultOpen={sequenceId === activeSequenceId}
              activeUnitId={unitId}
            />
          )) : sectionsIds.map((sectionId) => (
            <SidebarSection
              key={sectionId}
              courseId={courseId}
              section={sections[sectionId]}
              handleSelectSection={handleSelectSection}
            />
          ))}
        </ol>
      </section>
    </div>
  );
};

OutlineTray.ID = ID;

export default OutlineTray;
